import { useEffect, useState } from "react";
import { Loader2, Wallet } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminAdjustWalletMutation, useAdminUserWallet } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

export interface AdminAdjustWalletTarget {
  userId: string;
  name: string;
  email: string;
  memberCode?: string | null;
}

interface AdminAdjustWalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: AdminAdjustWalletTarget | null;
}

export function AdminAdjustWalletDialog({ open, onOpenChange, target }: AdminAdjustWalletDialogProps) {
  const { toast } = useToast();
  const adjustMutation = useAdminAdjustWalletMutation();
  const { data: wallet, isLoading: walletLoading, isError } = useAdminUserWallet(
    target?.userId ?? null,
    open && Boolean(target?.userId),
  );

  const [direction, setDirection] = useState<"credit" | "debit">("credit");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) {
      setDirection("credit");
      setAmount("");
      setReason("");
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!target) return;
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast({ title: "Enter a valid amount", variant: "destructive" });
      return;
    }
    if (reason.trim().length < 3) {
      toast({ title: "Reason is required", description: "Use at least 3 characters.", variant: "destructive" });
      return;
    }

    try {
      const updated = await adjustMutation.mutateAsync({
        userId: target.userId,
        amount: parsed,
        direction,
        reason: reason.trim(),
      });
      toast({
        title: direction === "credit" ? "Balance credited" : "Balance deducted",
        description: `New available balance: ₹${updated.availableBalance.toFixed(2)}`,
      });
      onOpenChange(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100%-1.5rem)] max-w-lg flex-col gap-0 overflow-hidden p-0 sm:rounded-xl">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            Adjust Wallet
          </DialogTitle>
          <DialogDescription>
            Add or deduct balance for this user. Every change is recorded in the wallet ledger.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {target && (
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <p className="text-sm font-semibold text-foreground">{target.name}</p>
              <p className="text-xs text-muted-foreground">{target.email}</p>
              {target.memberCode && (
                <p className="mt-1 font-mono text-xs text-muted-foreground">Member ID: {target.memberCode}</p>
              )}
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Available balance</span>
                {walletLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : isError || !wallet ? (
                  <span className="text-xs text-muted-foreground">Will create wallet on save</span>
                ) : (
                  <span className="font-semibold">₹{(wallet?.availableBalance ?? 0).toFixed(2)}</span>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Action</Label>
            <Select value={direction} onValueChange={(v) => setDirection(v as "credit" | "debit")}>
              <SelectTrigger className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">Add / Credit</SelectItem>
                <SelectItem value="debit">Deduct / Debit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adjust-amount">Amount (₹)</Label>
            <Input
              id="adjust-amount"
              type="number"
              min="1"
              step="0.01"
              className="h-11"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="adjust-reason">Reason</Label>
            <Textarea
              id="adjust-reason"
              placeholder="e.g. Manual top-up for order testing"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={adjustMutation.isPending || !target}
            variant={direction === "debit" ? "destructive" : "default"}
          >
            {adjustMutation.isPending ? "Saving…" : direction === "credit" ? "Credit Balance" : "Deduct Balance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
